import React, { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { FeedSpinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import { OutlineButton } from "../../components/shared/Button"
import { useBandCustomMailerTemplates } from "../../hooks/useBandCustomMailerTemplates"
import {
  createBandCustomMailerTemplate,
  updateBandCustomMailerTemplate,
  deleteBandCustomMailerTemplate,
  BandCustomMailerTemplateParams,
} from "../../requests/bandCustomMailerTemplates"
import { BandCustomMailerTemplate } from "../../types/bands"

interface MailerTemplatesProps {
  bandId: number
}

const NEW = "new"

const emptyForm: BandCustomMailerTemplateParams = {
  mailer_type: "concert_advance",
  name: "",
  content: "",
}

export const MailerTemplates: React.FC<MailerTemplatesProps> = ({ bandId }) => {
  const queryClient = useQueryClient()
  const { status, data, error } = useBandCustomMailerTemplates(bandId)
  const [selectedId, setSelectedId] = useState<number | typeof NEW | null>(null)
  const [form, setForm] = useState<BandCustomMailerTemplateParams>(emptyForm)

  const templates: BandCustomMailerTemplate[] =
    data?.band_custom_mailer_templates || []

  // Default to the first saved template once loaded, or the "Add New" form
  // if there aren't any yet.
  useEffect(() => {
    if (selectedId !== null) return
    if (templates.length > 0) {
      selectTemplate(templates[0].id)
    } else if (status === "success") {
      selectTemplate(NEW)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, templates.length])

  const invalidate = () =>
    queryClient.invalidateQueries(["band_custom_mailer_templates", bandId])

  const createMutation = useMutation(
    (params: BandCustomMailerTemplateParams) =>
      createBandCustomMailerTemplate(bandId, params),
    {
      onSuccess: (response) => {
        invalidate()
        setSelectedId(response.band_custom_mailer_template.id)
        toast.success("Saved")
      },
      onError: (e: Error) => {
        toast.error(e.message)
      },
    },
  )

  const updateMutation = useMutation(
    ({ id, params }: { id: number; params: BandCustomMailerTemplateParams }) =>
      updateBandCustomMailerTemplate(bandId, id, params),
    {
      onSuccess: () => {
        invalidate()
        toast.success("Saved")
      },
      onError: (e: Error) => {
        toast.error(e.message)
      },
    },
  )

  const deleteMutation = useMutation(
    (id: number) => deleteBandCustomMailerTemplate(bandId, id),
    {
      onSuccess: (_response, deletedId) => {
        invalidate()
        const remaining = templates.filter((t) => t.id !== deletedId)
        selectTemplate(remaining.length > 0 ? remaining[0].id : NEW)
        toast.success("Deleted")
      },
      onError: (e: Error) => {
        toast.error(e.message)
      },
    },
  )

  function selectTemplate(id: number | typeof NEW, list = templates) {
    setSelectedId(id)
    if (id === NEW) {
      setForm(emptyForm)
      return
    }
    const template = list.find((t) => t.id === id)
    setForm({
      mailer_type: template?.mailer_type || "concert_advance",
      name: template?.name || "",
      content: template?.content || "",
    })
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    selectTemplate(value === NEW ? NEW : Number(value))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedId === NEW) {
      createMutation.mutate(form)
    } else if (selectedId !== null) {
      updateMutation.mutate({ id: selectedId, params: form })
    }
  }

  const handleDelete = () => {
    if (selectedId === NEW || selectedId === null) return
    const template = templates.find((t) => t.id === selectedId)
    if (!template) return
    if (!window.confirm(`Delete "${template.name}"? This can't be undone.`)) {
      return
    }
    deleteMutation.mutate(selectedId)
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading

  return (
    <div>
      <h2 className="lg:hidden font-bold text-xl uppercase border-b-4 border-black mb-4">
        Advance Email Text
      </h2>
      {status === "loading" ? (
        <FeedSpinner />
      ) : status === "error" ? (
        <RequestError error={error} />
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Paste your Advance Email text here, and we&apos;ll include it for
            you when generating advance emails for your shows. Create as many as
            you like.
          </p>
          <div className="mb-4">
            <label className="block text-xs uppercase font-bold text-gray-700 mb-1">
              Template
            </label>
            <select
              className="w-full max-w-sm border rounded p-2 text-sm"
              value={selectedId === null ? "" : selectedId}
              onChange={handleSelectChange}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
              <option value={NEW}>+ Add New</option>
            </select>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="block text-xs uppercase font-bold text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                className="w-full border rounded p-2 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Standard, Outdoor Show, Late Night"
                required
              />
            </div>
            <div className="mb-2">
              <label className="block text-xs uppercase font-bold text-gray-700 mb-1">
                Content
              </label>
              <textarea
                className="w-full border rounded p-2 text-sm font-mono"
                rows={18}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Load-in at 6pm. Stage plot due one week before. Parking on Smith St..."
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              {selectedId !== NEW && (
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm text-red-600 hover:underline focus:outline-none mr-auto"
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                >
                  Delete
                </button>
              )}
              <OutlineButton
                type="submit"
                className="text-gray-800 text-sm"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </OutlineButton>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

export default MailerTemplates
